/* One delivery calendar for the product page, photo studio and cart.
 * Checkout remains Shopify's native manual dates. Rules must be updated together.
 */
(() => {
  'use strict';
  const iso = date => date.toISOString().slice(0, 10);
  function arrivalWindow(config, country, method, now) {
    const transit = config.markets[country]?.[method]?.transit;
    if (!transit || !Number.isFinite(now)) return null;
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: config.timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', hourCycle: 'h23'
    }).formatToParts(new Date(now)).map(p => [p.type, p.value]));
    const date = new Date(`${parts.year}-${parts.month}-${parts.day}T12:00:00Z`);
    if (iso(date) < config.calendarFrom || iso(date) > config.calendarThrough) return null;
    const holidays = new Set(config.holidays);
    const working = d => ![0, 6].includes(d.getUTCDay()) && !holidays.has(iso(d));
    const next = d => d.setUTCDate(d.getUTCDate() + 1);
    // After noon an order starts processing on the following working day.
    // Weekend orders start on Monday; do not apply the cutoff a second time.
    if (working(date) && Number(parts.hour) >= config.cutoffHour) next(date);
    while (!working(date)) next(date);
    const add = days => {
      const result = new Date(date);
      while (days > 0) { next(result); if (working(result)) days--; }
      return iso(result) <= config.calendarThrough ? iso(result) : null;
    };
    const start = add(config.fulfillmentDays + transit[0]);
    const end = add(config.fulfillmentDays + transit[1]);
    return start && end ? {start, end} : null;
  }
  // Exposed for deterministic contract checks without a browser or customer data.
  if (typeof module !== 'undefined' && module.exports) module.exports = {arrivalWindow};
  if (typeof document === 'undefined') return;
  const node = document.querySelector('[data-pbd-delivery-config]');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  // The old delivery app's add-time text cannot follow the checkout shipping choice.
  // Stop this one legacy property on new native product-form adds; keep all design data.
  document.addEventListener('formdata', event => {
    if (event.target instanceof HTMLFormElement && new URL(event.target.action).pathname.endsWith('/cart/add')) {
      event.formData.delete('properties[Estimated between]');
    }
  }, true);
  const loadedAt = performance.now();
  const now = () => config.serverTime * 1000 + performance.now() - loadedAt;
  const text = (key, values = {}) => Object.entries(values).reduce(
    (value, [name, replacement]) => value.replaceAll(`{${name}}`, replacement), config.messages[key] || key
  );
  function dateLabel(window) {
    if (!window) return text('dates_checkout');
    const formatter = new Intl.DateTimeFormat(config.locale, {day: 'numeric', month: 'short', timeZone: 'UTC'});
    const start = new Date(window.start + 'T12:00:00Z'), end = new Date(window.end + 'T12:00:00Z');
    const date = window.start === window.end ? formatter.format(start) : formatter.formatRange(start, end);
    return text('estimated_arrival', {date});
  }
  function priceLabel(rate) {
    if (!rate || !Number.isFinite(rate.amount)) return text('price_checkout');
    if (rate.amount === 0) return text('included');
    // Fixed local rates are exact. Do not invent converted checkout prices.
    if (rate.currency !== config.currency) return text('price_checkout');
    return text('shipping_price', {price: new Intl.NumberFormat(config.locale,
      {style: 'currency', currency: rate.currency}).format(rate.amount)});
  }
  function details(country = config.country) {
    const market = config.markets[country];
    return {
      standard: {price: priceLabel(market?.standard), arrival: dateLabel(arrivalWindow(config, country, 'standard', now()))},
      dhl: country === 'SE' || !market?.dhl ? null : {
        price: priceLabel(market.dhl), arrival: dateLabel(arrivalWindow(config, country, 'dhl', now()))
      }
    };
  }
  window.PBDDelivery = {details, arrivalWindow: (country, method) => arrivalWindow(config, country, method, now()), config};
  if (!customElements.get('pbd-delivery-estimate')) {
    customElements.define('pbd-delivery-estimate', class extends HTMLElement {
      connectedCallback() {
        this.render();
        this.timer = setInterval(() => this.render(), 60000);
      }
      disconnectedCallback() { clearInterval(this.timer); }
      render() {
        const data = details(this.dataset.country || config.country);
        for (const method of ['standard', 'dhl']) {
          const row = this.querySelector(`[data-delivery-method="${method}"]`);
          if (!row) continue;
          row.hidden = !data[method];
          if (data[method]) {
            row.querySelector('[data-delivery-price]').textContent = data[method].price;
            row.querySelector('[data-delivery-arrival]').textContent = data[method].arrival;
          }
        }
      }
    });
  }
  document.dispatchEvent(new CustomEvent('pbd:delivery-ready'));
})();
